import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FeatureGroup, LayerGroup, LayersControl, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';

import classnames from 'classnames';
import L from 'leaflet';
import isEmpty from 'lodash.isempty';
import { v4 as uuidv4 } from 'uuid';

import { ConditionalWrapper } from '../../helpers/map';

import { GEOMETRY_TYPE } from '../../constants/map';

import './styles.scss';

import BaseLayers from './BaseLayers';
import AppMapContainer from './Container';
import CustomLayers from './CustomLayers';
import EditButtons from './EditButtons';
import {
  EDIT_CONTROL_DRAW_OPTIONS,
  EDIT_CONTROL_POSITION,
  LAYERS_CONTROL_POSITION,
  SHAPE_OPTIONS,
  ZOOM_MAX,
  customizeButtonLabels
} from './constants';

const { Overlay } = LayersControl;
const DELAY = 300;

function SiteLayer(props) {
  const map = useMap();
  const featureGroupRef = useRef(null);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [state, setState] = useState({
    color: props.color,
    enableDraw: !!props.new
  });

  const initializeFeatureGroup = () => {
    if (!featureGroupRef?.current) return;

    const fg = featureGroupRef.current;

    fg.clearLayers();

    if (!props.site?.geo_json?.geometry || isEmpty(props.site?.geo_json)) return;

    props.site?.geo_json?.properties?.radius
      ? drawCircle(fg, props.site?.geo_json?.geometry?.coordinates, props.site?.geo_json?.properties?.radius)
      : drawPolygon(fg, props.site?.geo_json);
  };

  function drawCircle(fg, coordinates, radius) {
    const [lat, lng] = [coordinates?.at(1), coordinates?.at(0)];
    const layer = L.circle([lat, lng], {
      ...SHAPE_OPTIONS,
      color: state.color,
      fillColor: state.color,
      radius
    });

    fg.addLayer(layer);
  }

  function drawPolygon(fg, geoJson) {
    L.geoJSON(geoJson, {
      style: {
        ...SHAPE_OPTIONS,
        color: state.color,
        fillColor: state.color
      }
    }).eachLayer(layer => fg.addLayer(layer));
  }

  const handleFullScreenChange = () => {
    setIsFullScreen(!!document.fullscreenElement);
  };

  const clickFinishButton = () => {
    const ul = document.querySelector('.leaflet-draw-actions.leaflet-draw-actions-top');
    const firstLi = ul?.querySelector('li'); // Finish button

    if (firstLi) {
      const link = firstLi.querySelector('a');
      link?.click();
    }
  };

  const clickEditButton = () => {
    const editButton = document.querySelector('a.leaflet-draw-edit-edit');

    if (editButton) editButton.click();
  };

  const handleColorChange = useCallback(() => {
    if (!featureGroupRef.current) return;

    if (state.color !== props.color) {
      featureGroupRef.current.eachLayer(layer => {
        if (typeof layer.setStyle === 'function') {
          layer.setStyle({ color: props.color, fillColor: props.color, zIndex: 9999 });
        }
      });

      setState(prevState => ({ ...prevState, color: props.color }));
    }
  }, [props.color]);

  useEffect(() => {
    customizeButtonLabels();

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (props.farm && props.site) {
      initializeFeatureGroup();
      tick(props.farm, props.site);
    }
  }, [props.farm, props.site, props.selected]);

  useEffect(() => {
    toggleEditMode();

    setTimeout(props.editing ? clickEditButton : clickFinishButton, DELAY);
  }, [props.editing]);

  useEffect(() => {
    handleColorChange();
    // setTimeout(clickEditButton, DELAY);
  }, [handleColorChange, props.color]);

  function toggleEditMode() {
    if (!map) return;

    const mapContainer = map?.getContainer();

    props.editing ? mapContainer.classList.add('editing') : mapContainer.classList.remove('editing');
  }

  function calculateBoundsForCircle(geoJson) {
    if (isEmpty(geoJson?.geometry)) return;

    let bounds = {};
    const { coordinates } = geoJson.geometry;
    const radius = geoJson.properties?.radius || 0;

    if (coordinates?.length === 2 && radius > 0) {
      const [lng, lat] = coordinates;
      const center = L.latLng(lat, lng);

      bounds = center.toBounds(radius * 2);

      return bounds;
    }
  }

  function calculateBoundsForPolygon(geoJson) {
    const group = new L.geoJson(geoJson);
    let bounds = group.getBounds();

    if (bounds.isValid()) {
      const corner2 = L.latLng(parseFloat(bounds._southWest?.lat), parseFloat(bounds._southWest?.lng));
      const corner1 = L.latLng(parseFloat(bounds._northEast?.lat), parseFloat(bounds._northEast?.lng));

      bounds = L.latLngBounds(corner1, corner2);

      return bounds;
    }
  }

  function calculateMapBounds(geoData) {
    // geoData can be FeatureCollection or GeoJSON
    const source = geoData?.geometry ? geoData : geoData?.features?.at(0);

    if (!source) return;

    const bounds =
      source.geometry?.type === GEOMETRY_TYPE.POINT && source.properties?.radius
        ? calculateBoundsForCircle(source)
        : calculateBoundsForPolygon(source);

    if (map) {
      const center = bounds.getCenter();
      map?.setView(center, Math.min(map?.getBoundsZoom(bounds), ZOOM_MAX));
    }
  }

  function tick(farm, site) {
    const geofences = { type: 'FeatureCollection', features: [] };
    const masterGeofences = { type: 'FeatureCollection', features: [] };
    const sites = { type: 'FeatureCollection', features: [] };

    if (isEmpty(site?.geo_json)) {
      featureGroupRef?.current?.clearLayers();
    }

    if (!farm?.meta?.features?.length && isEmpty(site?.geo_json)) return;

    farm?.meta?.features?.forEach(feature => {
      const target = feature.is_master ? masterGeofences.features : geofences.features;
      target.push({ ...feature.geo_json, color: feature.color, id: uuidv4() });
    });

    farm?.meta?.sites?.forEach(s => {
      if (site?.geo_json?.properties?.identifier !== s.identifier) {
        sites.features.push({ ...s.geo_json, color: s.color, id: uuidv4() });
      }
    });

    setState(prevState => ({
      ...prevState,
      features_geo: geofences,
      features_master: masterGeofences,
      features_sites: { ...sites, features: sites.features.filter(f => f.properties?.id !== site.id) },
      farm,
      site
    }));

    const center = site?.geo_json?.type
      ? site?.geo_json
      : props.selected?.geo_json
        ? props.selected?.geo_json
        : masterGeofences;

    calculateMapBounds(center);
  }

  function handleCreateShape(e) {
    setState(prevState => ({ ...prevState, enableDraw: !!props.new, leaflet_id: e.layer._leaflet_id }));

    const layers = L.layerGroup().addLayer(e.layer);

    layers.eachLayer(layer => {
      if (props.onCreate) {
        const geoJson = layer.toGeoJSON();

        geoJson.properties.radius = layer._mRadius ? layer._mRadius : layer._radius;

        props.onCreate(geoJson);
      }
    });
  }

  function handleEditShape(layerGroup) {
    layerGroup.layers.eachLayer(layer => {
      if (props.onEdit) {
        const geoJson = layer.toGeoJSON();

        if (layer._mRadius) {
          geoJson.properties.radius = layer._mRadius;
        } else if (layer._radius) {
          geoJson.properties.radius = layer._radius;
        }
        props.onEdit(geoJson);
      }
    });

    setState(prevState => ({ ...prevState, enableDraw: false }));
  }

  function handleDeleteShape() {
    setState(prevState => ({ ...prevState, enableDraw: true }));
    if (props.handleDelete) props.handleDelete?.();
  }

  /**
   * Not used, remained to keep the structure and if we change the behavior
   */
  function handleSave() {
    props.handleSaveEditing();
    exitFullScreenMode();
  }

  function exitFullScreenMode() {
    if (map && map?.fullscreenControl) {
      document.exitFullscreen();
    }
  }

  return (
    <LayersControl position={LAYERS_CONTROL_POSITION}>
      <BaseLayers />
      <CustomLayers
        features={state.features_geo}
        features_master={state.features_master}
        features_sites={state.features_sites}
        grazing={props.grazing}
      />

      <ConditionalWrapper
        condition={!props.new}
        wrapper={children => (
          <Overlay checked name="Current Site">
            {children}
          </Overlay>
        )}>
        <LayerGroup>
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              draw={{
                ...EDIT_CONTROL_DRAW_OPTIONS,
                circle: props.editing &&
                  state.enableDraw && {
                    shapeOptions: { ...SHAPE_OPTIONS, color: state.color, fillColor: state.color }
                  },
                polygon: props.editing &&
                  state.enableDraw && {
                    shapeOptions: { ...SHAPE_OPTIONS, color: state.color, fillColor: state.color }
                  }
              }}
              edit={{ featureGroup: featureGroupRef?.current, edit: true, remove: true }}
              position={EDIT_CONTROL_POSITION}
              onCreated={handleCreateShape}
              onDeleted={handleDeleteShape}
              onEdited={handleEditShape}
            />
          </FeatureGroup>
        </LayerGroup>

        {isFullScreen && props.editing && (
          <EditButtons handleCancel={props.handleCancelEditing} handleReturn={exitFullScreenMode} />
        )}
      </ConditionalWrapper>
    </LayersControl>
  );
}

function Map(props) {
  return (
    <AppMapContainer
      height={400}
      className={classnames({ 'edit-controls-visible': props.new })}
      loading={props?.loading}>
      <SiteLayer {...props} />
    </AppMapContainer>
  );
}

export default memo(Map);
